import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import qs from 'query-string';
import { dataCentralApi } from '@/api/client';
import { toFilters } from '@/api/customers';
import type { ResponseArray, ResponseObject } from '@/types/shared';

/**
 * Invoices — global standalone module (`modules/invoice` on the v4 backend,
 * base group `/invoices`). Field names / endpoints are lifted verbatim so
 * requests hit unchanged. Shapes verified against live staging. This is the
 * GLOBAL module; the customer-scoped tab lives in `customer-invoices.ts`.
 */

/* ── Enums ─────────────────────────────────────────────────────────────── */

/** DB `invoice_status`. */
export type InvoiceStatus =
  | 'waiting-confirm'
  | 'approved'
  | 'paid'
  | 'cancelled'
  | 'rejected'
  | 'done'
  | 'refunded';

/** DB `invoice_type`. */
export type InvoiceType = 'deposit' | 'refund-deposit-of-customer' | 'draft' | 'default';

/** `invoice_sale_type`. */
export type InvoiceSaleType = 'new-sell' | 'upsell' | 'renewable';

/* ── Types ─────────────────────────────────────────────────────────────── */

/** Embedded customer summary on an invoice row. */
export interface InvoiceCustomer {
  customer_id: number;
  customer_name: string;
  company_name?: string | null;
  account_manager_codes?: number[];
  account_manager_names?: string[];
}

/** Embedded service summary on an invoice row. */
export interface InvoiceService {
  service_id?: number;
  service_name?: string;
  product_group_name?: string;
  [key: string]: unknown;
}

/** `GET /invoices` row (`GetInvoicesRow`). */
export interface InvoiceRow {
  invoice_id: number;
  invoice_code: string;
  invoice_status: InvoiceStatus;
  invoice_type: InvoiceType;
  invoice_sale_type?: InvoiceSaleType;
  customer?: InvoiceCustomer | null;
  service?: InvoiceService | null;
  invoice_amount: number;
  invoice_vat: number;
  invoice_total: number;
  invoice_apply_startdate?: string;
  invoice_apply_enddate?: string;
  invoice_duedate?: string;
  invoice_payment_at?: string | null;
  invoice_mail_count?: number;
  invoice_billing_scope?: string;
  invoice_exporter_code?: string | null;
  invoice_note?: string | null;
  invoice_description?: string | null;
  contract_role?: string;
  contract_direction?: string;
  invoice_items?: unknown[];
  revenue_confirmed?: boolean;
}

/** pgtype nullable wrapper the detail endpoint uses for some optional scalars. */
export interface NullableWrap<T> {
  valid?: boolean;
  [key: string]: unknown;
  invoice_sale_type?: T;
}

/**
 * `GET /invoices/:id` — full record (adds commission / revenue / exporter blocks).
 * NOTE the detail endpoint diverges from the list row for a few fields (verified
 * live): `invoice_sale_type` comes back as a `{ invoice_sale_type, valid }` wrapper
 * (not the plain string the list returns), and `employee_names` / `employee_codes`
 * are comma-joined STRINGS (not arrays). Use `resolveSaleType` to normalize.
 */
export interface InvoiceDetail extends Omit<InvoiceRow, 'invoice_sale_type' | 'customer'> {
  invoice_sale_type?: InvoiceSaleType | NullableWrap<InvoiceSaleType | string> | null;
  customer?: (InvoiceCustomer & { customer_email?: string; customer_language?: string; is_grey_market?: boolean }) | null;
  customer_id?: number;
  customer_contact_id?: number;
  service_id?: number;
  service_ids?: number[] | null;
  invoice_currency?: string;
  invoice_exchange_rate?: number;
  invoice_payment_method?: string | null;
  invoice_renewable?: boolean;
  invoice_first_bill_of_service?: boolean;
  invoice_commissions?: unknown;
  invoice_exporter_id?: number | null;
  invoice_exporter_status?: string | null;
  revenue_confirmed_at?: string | null;
  revenue_confirmed_by?: number | null;
  invoicable?: boolean;
  invoicable_changed_at?: string | null;
  invoicable_changed_by?: number | null;
  employee_commission_ids?: number[] | null;
  /** Comma-joined string on the detail endpoint (not an array). */
  employee_names?: string;
  /** Comma-joined string on the detail endpoint (not an array). */
  employee_codes?: string;
  kpi_rates?: Record<string, unknown> | null;
  created_at?: string;
  updated_at?: string;
}

/**
 * `GET /invoices/statistical` — grouped rows; aggregate client-side into status
 * cards (sum `total_amount`, sum `count`).
 */
export interface InvoiceStatisticalRow {
  invoice_status: InvoiceStatus;
  revenue_confirmed: boolean;
  invoice_type: InvoiceType;
  total_amount: number;
  count: number;
}

/** `GET /invoices/:id/items` row (line item). */
export interface InvoiceItemRow {
  invoice_item_id?: number;
  invoice_item_name?: string;
  invoice_item_quantity?: number;
  invoice_item_unit_price?: number;
  invoice_item_tax?: number;
  invoice_item_amount?: number;
  // Richer fields returned by GET /invoices/:id/items (verified live).
  invoice_item_description?: string | null;
  invoice_item_tags?: string | null;
  invoice_item_discount?: number | null;
  invoice_item_apply_startdate?: string | null;
  invoice_item_apply_enddate?: string | null;
  invoice_item_before_exchange_rate?: number | null;
  exchange_rate?: number | null;
  currency?: string | null;
  tax_percentage?: number | null;
  invoice_item_taxable?: boolean | null;
  service_id?: number | null;
  service_name?: string | null;
  usage_quantity?: number | null;
  usage_price?: number | null;
  [key: string]: unknown;
}

/** `GET /invoices/:id/transactions` row (payment). */
export interface InvoiceTransactionRow {
  transaction_id?: number;
  transaction_date?: string;
  transaction_method?: string;
  transaction_amount?: number;
  transaction_note?: string;
  created_at?: string;
  // Richer fields returned by GET /invoices/:id/transactions (verified live).
  invoice_transaction_id?: number;
  employee_name?: string | null;
  transaction_fee?: number | null;
  transaction_status?: string | null;
  exchange_rate?: number | null;
  currency?: string | null;
  invoice_transaction_before_exchange_rate?: number | null;
  [key: string]: unknown;
}

/** `GET /invoices/:id/logs` row (update history). */
export interface InvoiceLogRow {
  log_id?: number;
  log_type?: string;
  log_description?: string;
  employee_name?: string;
  created_at?: string;
  metadata?: unknown;
  // Actual fields returned by GET /invoices/:id/logs (verified live).
  invoice_update_id?: number;
  invoice_update_type?: string;
  invoice_update_description?: string | null;
  invoice_update_data?: unknown;
  employee_id?: number;
  [key: string]: unknown;
}

/**
 * `GET /mailers/tasks/invoices/:id` row — one mail dispatch task for an invoice.
 * Shape verified from legacy `InvoiceMailTask` type + endpoint probe.
 */
export interface InvoiceMailTask {
  task_id: number;
  task_jid?: string;
  task_uuid?: string;
  task_name?: string;
  task_description?: string;
  task_status?: string;
  task_scope?: string;
  task_bind?: string;
  task_referrer_id?: number;
  task_referrer_jid?: string;
  task_referrer_type?: string;
  task_delivery_time?: string | null;
  task_created_at?: string;
  task_updated_at?: string;
  task_completed_at?: string | null;
  metadata?: {
    subject?: string;
    sender?: string | null;
    mailer?: {
      recipients?: string;
      cc?: string;
      bcc?: string;
    };
    invoice?: unknown;
    metadata?: unknown;
    [key: string]: unknown;
  };
}

/* ── Params ────────────────────────────────────────────────────────────── */

export interface InvoiceListParams {
  page?: number;
  page_size?: number;
  q?: string;
  sort_by?: string;
  sort_order?: 'asc' | 'desc';
  filters?: Record<string, unknown>;
}

export interface InvoiceMailHistoryParams {
  page?: number;
  page_size?: number;
  q?: string;
}

function listQuery(params: InvoiceListParams): string {
  const { filters, ...rest } = params;
  const q: Record<string, string | number> = {};
  for (const [k, v] of Object.entries(rest)) {
    if (v !== undefined && v !== '') q[k] = v as string | number;
  }
  if (filters && Object.keys(filters).length > 0) q.filters = toFilters(filters);
  return qs.stringify(q);
}

/* ── Query keys ────────────────────────────────────────────────────────── */

export const invoiceKeys = {
  all: ['invoices'] as const,
  list: (params: InvoiceListParams) => ['invoices', 'list', params] as const,
  statistical: (params: InvoiceListParams) => ['invoices', 'statistical', params] as const,
  detail: (id: number) => ['invoices', 'detail', id] as const,
  items: (id: number) => ['invoices', id, 'items'] as const,
  transactions: (id: number) => ['invoices', id, 'transactions'] as const,
  logs: (id: number) => ['invoices', id, 'logs'] as const,
  totalPaid: (id: number) => ['invoices', id, 'paymented'] as const,
  mailHistory: (id: number, params: InvoiceMailHistoryParams) => ['invoices', id, 'mail-history', params] as const,
};

/* ── Queries ───────────────────────────────────────────────────────────── */

/** `GET /invoices` — global invoice list. */
export function useInvoices(params: InvoiceListParams = {}) {
  return useQuery({
    queryKey: invoiceKeys.list(params),
    queryFn: async (): Promise<ResponseArray<InvoiceRow>> => {
      const res = await dataCentralApi.get<ResponseArray<InvoiceRow>>(
        `/invoices?${listQuery({ page: 1, page_size: 20, ...params })}`,
      );
      return res.data;
    },
  });
}

/** `GET /invoices/statistical` — grouped rows for the stat band. */
export function useInvoiceStatistical(params: InvoiceListParams = {}) {
  return useQuery({
    queryKey: invoiceKeys.statistical(params),
    queryFn: async (): Promise<ResponseArray<InvoiceStatisticalRow>> => {
      const res = await dataCentralApi.get<ResponseArray<InvoiceStatisticalRow>>(
        `/invoices/statistical?${listQuery(params)}`,
      );
      return res.data;
    },
  });
}

/** `GET /invoices/:id` — full invoice record. */
export function useInvoiceDetail(id: number) {
  return useQuery({
    queryKey: invoiceKeys.detail(id),
    enabled: !!id,
    queryFn: async (): Promise<ResponseObject<InvoiceDetail>> => {
      const res = await dataCentralApi.get<ResponseObject<InvoiceDetail>>(`/invoices/${id}`);
      return res.data;
    },
  });
}

/** `GET /invoices/:id/items` — line items. */
export function useInvoiceItems(id: number) {
  return useQuery({
    queryKey: invoiceKeys.items(id),
    enabled: !!id,
    queryFn: async (): Promise<ResponseArray<InvoiceItemRow>> => {
      const res = await dataCentralApi.get<ResponseArray<InvoiceItemRow>>(
        `/invoices/${id}/items?page=1&page_size=100`,
      );
      return res.data;
    },
  });
}

/** `GET /invoices/:id/transactions` — payments. */
export function useInvoiceTransactions(id: number) {
  return useQuery({
    queryKey: invoiceKeys.transactions(id),
    enabled: !!id,
    queryFn: async (): Promise<ResponseArray<InvoiceTransactionRow>> => {
      const res = await dataCentralApi.get<ResponseArray<InvoiceTransactionRow>>(
        `/invoices/${id}/transactions?page=1&page_size=100`,
      );
      return res.data;
    },
  });
}

/** `GET /invoices/:id/logs` — update history. */
export function useInvoiceLogs(id: number) {
  return useQuery({
    queryKey: invoiceKeys.logs(id),
    enabled: !!id,
    queryFn: async (): Promise<ResponseArray<InvoiceLogRow>> => {
      // The backend returns `results: null` without an explicit page_size (a
      // small/absent size yields a nil slice) — request a full page so the
      // update history actually renders.
      const res = await dataCentralApi.get<ResponseArray<InvoiceLogRow>>(
        `/invoices/${id}/logs?page=1&page_size=100`,
      );
      return res.data;
    },
  });
}

/** `GET /mailers/tasks/invoices/:id` — paginated mail dispatch history for an invoice. */
export function useInvoiceMailHistory(id: number, params: InvoiceMailHistoryParams = {}) {
  return useQuery({
    queryKey: invoiceKeys.mailHistory(id, params),
    enabled: !!id,
    queryFn: async (): Promise<ResponseArray<InvoiceMailTask>> => {
      const q = qs.stringify({ page: 1, page_size: 20, ...params });
      const res = await dataCentralApi.get<ResponseArray<InvoiceMailTask>>(
        `/mailers/tasks/invoices/${id}?${q}`,
      );
      return res.data;
    },
  });
}

/** `GET /invoices/:id/paymented` — total paid amount. */
export function useInvoiceTotalPaid(id: number) {
  return useQuery({
    queryKey: invoiceKeys.totalPaid(id),
    enabled: !!id,
    queryFn: async (): Promise<ResponseObject<{ total_paid?: number; [key: string]: unknown }>> => {
      const res = await dataCentralApi.get<ResponseObject<{ total_paid?: number }>>(
        `/invoices/${id}/paymented`,
      );
      return res.data;
    },
  });
}

/* ── Mutations ─────────────────────────────────────────────────────────── */

/** `POST /invoices/:id/approved` — `{ status: 'invoicable' | 'non-invoicable' }`. */
export function useApproveInvoice(id: number) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (payload: { status: 'invoicable' | 'non-invoicable' }): Promise<ResponseObject<InvoiceDetail>> => {
      const res = await dataCentralApi.post<ResponseObject<InvoiceDetail>>(
        `/invoices/${id}/approved`,
        payload,
      );
      return res.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: invoiceKeys.detail(id) });
      queryClient.invalidateQueries({ queryKey: invoiceKeys.all });
    },
  });
}

/** `POST /invoices/:id/cancelled` — `{ category, message }`. */
export function useCancelInvoice(id: number) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (payload: { category: string; message: string }): Promise<ResponseObject<InvoiceDetail>> => {
      const res = await dataCentralApi.post<ResponseObject<InvoiceDetail>>(
        `/invoices/${id}/cancelled`,
        payload,
      );
      return res.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: invoiceKeys.detail(id) });
      queryClient.invalidateQueries({ queryKey: invoiceKeys.all });
    },
  });
}

/** `GET /invoices/:id/prefill-email` — recipients/subject/body prefill for Send-mail. */
export function useInvoicePrefillEmail(id: number, enabled = false) {
  return useQuery({
    queryKey: ['invoices', id, 'prefill-email'],
    enabled: !!id && enabled,
    queryFn: async (): Promise<ResponseObject<Record<string, unknown>>> => {
      const res = await dataCentralApi.get<ResponseObject<Record<string, unknown>>>(
        `/invoices/${id}/prefill-email`,
      );
      return res.data;
    },
  });
}

/** `POST /invoices/:id/send-mail` — `{ recipients, cc?, bcc?, ... }`. */
export function useSendInvoiceMail(id: number) {
  return useMutation({
    mutationFn: async (payload: Record<string, unknown>): Promise<ResponseObject<Record<string, unknown>>> => {
      const res = await dataCentralApi.post<ResponseObject<Record<string, unknown>>>(
        `/invoices/${id}/send-mail`,
        payload,
      );
      return res.data;
    },
  });
}

/** `POST /invoices/merge` — `{ invoice_ids[] }` (same type/customer/scope/sale_type, none paid). */
export function useMergeInvoices() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (payload: { invoice_ids: number[] }): Promise<ResponseObject<InvoiceDetail>> => {
      const res = await dataCentralApi.post<ResponseObject<InvoiceDetail>>('/invoices/merge', payload);
      return res.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: invoiceKeys.all });
    },
  });
}

/** `GET /invoices/:id/preview.statement.html` — rendered statement HTML for the mail preview. */
export function useInvoiceStatementPreview(id: number, enabled = false) {
  return useQuery({
    queryKey: ['invoices', id, 'preview-statement'],
    enabled: !!id && enabled,
    queryFn: async (): Promise<string> => {
      const res = await dataCentralApi.get<string>(`/invoices/${id}/preview.statement.html`, {
        responseType: 'text',
      });
      return res.data;
    },
  });
}

/** `DELETE /invoices/:id?category=&message=` — delete a waiting-confirm invoice. */
export function useDeleteInvoice(id: number) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (payload: { category: string; message?: string }): Promise<unknown> => {
      const res = await dataCentralApi.delete(`/invoices/${id}`, {
        params: { category: payload.category, message: payload.message ?? '' },
      });
      return res.data;
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: invoiceKeys.all }),
  });
}

/** `POST /invoices/:id/rollback` — `{ message }` (paid / done → approved). */
export function useRollbackInvoice(id: number) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (payload: { message: string }): Promise<unknown> => {
      const res = await dataCentralApi.post(`/invoices/${id}/rollback`, payload);
      return res.data;
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: invoiceKeys.all }),
  });
}

/** `POST /invoices/:id/invoice-updates` — append a manual update-log entry. */
export function useAddInvoiceUpdate(id: number) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (payload: {
      invoice_update_description: string;
      invoice_update_type?: string;
    }): Promise<unknown> => {
      const res = await dataCentralApi.post(`/invoices/${id}/invoice-updates`, {
        invoice_update_type: payload.invoice_update_type ?? 'manual',
        invoice_update_description: payload.invoice_update_description,
      });
      return res.data;
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: invoiceKeys.all }),
  });
}

/** Body for `POST /invoices/:id/transactions` (Mark-Paid — record a payment). */
export interface CreateTransactionPayload {
  transaction_amount: number;
  transaction_method?: string;
  transaction_fee?: number;
  transaction_status?: string;
  transaction_note?: string;
  exchange_rate?: number;
  currency?: string;
  invoice_transaction_before_exchange_rate?: number;
  credit_id?: number;
  /** Payment date, `YYYY-MM-DD`. */
  invoice_payment_at?: string;
  currency_of_invoice?: string;
}

/** `POST /invoices/:id/transactions` — record a payment against the invoice. */
export function useCreateInvoiceTransaction(id: number) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (payload: CreateTransactionPayload): Promise<unknown> => {
      const res = await dataCentralApi.post(`/invoices/${id}/transactions`, payload);
      return res.data;
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: invoiceKeys.all }),
  });
}
